import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Text, Icon, PrimaryButton, TextButton, Alert } from "@artprov/design-system";
import MatchingBancarioDetail from "./MatchingBancarioDetail";
import { getMatchingBancarioDetalle, updateEmailBancario } from "../../../services/matchingBancario.service";
import { errorService } from "../../../common/utils/functions/error.service";

function Detalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [detalleData, setDetalleData] = useState(null);
    const [error, setError] = useState(null);

    // Estado para alertas
    const [alert, setAlert] = useState({
        show: false,
        variant: "success",
        title: "",
        description: "",
        descriptionStyle: "break-words whitespace-pre-line",
    });

    const hideAlert = () => {
        setAlert({ ...alert, show: false });
    };

    const showAlert = (variant, title, description) => {
        setAlert({
            show: true,
            variant,
            title,
            description,
            descriptionStyle: "break-words whitespace-pre-line",
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            setLoading(true);
            setError(null);

            try {
                const data = await getMatchingBancarioDetalle(id);
                setDetalleData(data);
            } catch (err) {
                errorService.handleError(err, {
                    userMessage: "No pudimos cargar la información del matching bancario.",
                    origen: "Detalle.fetchData",
                });
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // Handler para actualización exitosa
    const handleUpdateSuccess = async (payload) => {
        setSaving(true);

        try {
            const response = await updateEmailBancario(id, payload);

            // Actualizar datos locales con la respuesta
            setDetalleData({
                ...detalleData,
                matching: response.matching,
                emailBancario: response.emailBancario,
            });

            // Mostrar alerta de éxito
            showAlert(
                "success",
                "",
                "Los cambios en Información mail embargo fueron guardados exitosamente."
            );
        } catch (err) {
            errorService.handleError(err, {
                userMessage: "No pudimos actualizar los datos del email bancario.",
                origen: "Detalle.handleUpdateSuccess",
            });

            // Mostrar alerta de error
            showAlert(
                "error",
                "",
                err.response?.data?.message || "Hubo un error al guardar los cambios de Información mail embargo. \n Intentá nuevamente más tarde."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-[32px] lg:mx-[95px] mx-[40px]">
            {/* Spinner de guardado (pantalla completa) */}
            {saving && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-50">
                    <Icon variant="loading" classNameIcon="size-16 text-[64px]" />
                </div>
            )}

            {/* Alerta posicionada absolutamente */}
            {alert.show && (
                <div className="fixed top-3 right-4 z-50">
                    <Alert
                        variant={alert.variant}
                        title={alert.title}
                        description={alert.description}
                        onClose={hideAlert}
                        autoDismiss={true}
                        containerStyle="w-[336px] h-[86px]"
                        descriptionStyle={alert.descriptionStyle}
                    />
                </div>
            )}

            <div className="space-y-6">
                {/* Header con botón volver */}
                <div className="flex items-center gap-4">
                    <TextButton
                        onClick={() => navigate('/')}
                        IconLeft="keyboard_arrow_left"
                        size="extra-small"
                        descriptionTextVariant="button3"
                        classNameIcon="!text-[16px]"
                    >
                        Volver
                    </TextButton>
                </div>

                {/* Contenido */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Icon icon="progress_activity" size="large" variant="loading" className="text-primary-30" />
                        <Text variant="body1" className="text-tertiary-70 mt-4">
                            Cargando datos...
                        </Text>
                    </div>
                )}

                {error && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Icon icon="error" size="large" className="text-error-50" />
                        <Text variant="body1" className="text-error-50 mt-4">
                            Error: {error}
                        </Text>
                        <PrimaryButton
                            onClick={() => navigate('/')}
                            className="mt-4"
                        >
                            Volver al inicio
                        </PrimaryButton>
                    </div>
                )}

                {!loading && !error && detalleData && (
                    <div className="max-w-full">
                        <MatchingBancarioDetail
                            matchingData={detalleData.matching}
                            emailBancario={detalleData.emailBancario}
                            extractoBancario={detalleData.extractoBancario}
                            documento={detalleData.documento}
                            emailBancarioId={detalleData.emailBancario?.id}
                            onUpdateSuccess={handleUpdateSuccess}
                        />
                    </div>
                )}

                {!loading && !error && !detalleData && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Icon icon="search_off" size="large" className="text-tertiary-70" />
                        <Text variant="body1" className="text-tertiary-70 mt-4">
                            No se encontraron datos
                        </Text>
                        <PrimaryButton
                            onClick={() => navigate('/')}
                            className="mt-4"
                        >
                            Volver al inicio
                        </PrimaryButton>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Detalle;
